import { Group, Modal, Stack, Text } from "@mantine/core"
import { MIME_TYPES } from "@mantine/dropzone"
import { IconCloudUpload } from "@tabler/icons-react"
import { Button, DEFAULT_MAX_SIZE, DocumentUploader } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import type { FindIngestionsFiltersDto } from "~/gen/api"
import { EcoModulationFilePreview } from "~/pages/Brand/DataTransfer/Import/components/EcoModulationFilePreview"
import { EcoModulationImportResultSummary } from "~/pages/Brand/DataTransfer/Import/components/EcoModulationImportResultSummary"
import { EcoModulationImportSummary } from "~/pages/Brand/DataTransfer/Import/components/EcoModulationImportSummary"
import { IngestionSortsType } from "~/pages/Brand/DataTransfer/Import/components/IngestionsSorts"
import { useEcoModulationImportFlow } from "~/pages/Brand/DataTransfer/Import/hooks/useEcoModulationImportFlow"
import { config } from "~/pages/Brand/DataTransfer/config.ts"
import { formatFileSize } from "~/utils/formatFileSize"

type EcoModulationImportModalProps = {
  opened: boolean
  onClose: () => void
  sorts: IngestionSortsType
  filters: FindIngestionsFiltersDto
}

export const EcoModulationImportModal = ({
  opened,
  onClose,
  sorts,
  filters,
}: EcoModulationImportModalProps) => {
  const { t } = useTranslation(["dataTransfer", "common"])
  const flow = useEcoModulationImportFlow({ onClose, sorts, filters })

  return (
    <Modal
      opened={opened}
      onClose={flow.handleClose}
      title={t("dataTransfer:import.ecoModulation.summaryModal.title")}
      centered
      size="lg"
    >
      <Stack gap="var(--tfg-organism-padding-y)" miw={0}>
        {!flow.hasConfirmLaunched && (
          <DocumentUploader
            onDrop={flow.handleDrop}
            maxFiles={1}
            multiple={false}
            width="100%"
            maxSize={DEFAULT_MAX_SIZE}
            accept={[MIME_TYPES.csv]}
            icon={<IconCloudUpload size={32} stroke={1.5} />}
            title={t(
              "dataTransfer:import.ecoModulation.summaryModal.dropzone.idle"
            )}
            helpText={t(
              "dataTransfer:import.ecoModulation.summaryModal.dropzone.help",
              { maxSize: formatFileSize() }
            )}
            titleSize="sm"
            helpTextSize="xs"
            justify="center"
            disabled={flow.isConfirming || flow.isConfirmRequestPending}
            onReject={flow.handleReject}
          />
        )}

        {flow.selectedFile !== null && (
          <Stack gap="var(--tfg-molecule-padding-y)">
            <EcoModulationFilePreview
              file={flow.selectedFile}
              hasFileError={flow.hasFileError}
              fileErrorDetail={flow.fileErrorDetail}
              isFileBusy={flow.isFileBusy}
              busyLabel={
                flow.isConfirming
                  ? t(
                      "dataTransfer:import.ecoModulation.summaryModal.confirming"
                    )
                  : t(
                      "dataTransfer:import.ecoModulation.summaryModal.validating"
                    )
              }
              isFileComplete={flow.isFileComplete}
              canRemove={!flow.hasConfirmLaunched}
              isRemoveDisabled={
                flow.isConfirming || flow.isConfirmRequestPending
              }
              isProgressActive={flow.isProgressActive}
              progressRunId={flow.progressRunId}
              onRemove={flow.handleRemoveFile}
            />
            {flow.hasRequestError && flow.isFormatRejectError && (
              <Button
                component="a"
                href={config.ECO_MODULATION_IMPORT_TEMPLATE}
                variant="tertiary"
                size="compact-xs"
                fz="xs"
                px={0}
                style={{ alignSelf: "flex-start", height: "auto" }}
              >
                {t(
                  "dataTransfer:import.ecoModulation.summaryModal.downloadTemplateHint"
                )}
              </Button>
            )}
          </Stack>
        )}

        {flow.shouldShowValidationSummary && flow.response !== null && (
          <Stack gap="var(--tfg-molecule-padding-y)" w="100%" miw={0}>
            <EcoModulationImportSummary
              summary={flow.response.summary}
              validationErrors={flow.validationErrors}
            />
          </Stack>
        )}

        {flow.hasConfirmLaunched && flow.processRowErrors.length > 0 && (
          <EcoModulationImportResultSummary
            productsImported={flow.failedIngestionProductCount}
            errors={flow.processRowErrors}
          />
        )}

        {flow.shouldShowFollowInTableHint && (
          <Text size="xs" c="dimmed">
            {flow.isConfirming
              ? t(
                  "dataTransfer:import.ecoModulation.summaryModal.followInTableWhileRunning"
                )
              : t(
                  "dataTransfer:import.ecoModulation.summaryModal.followInTableAfterError"
                )}
          </Text>
        )}

        <Group
          grow={!flow.hasConfirmLaunched}
          gap="var(--tfg-molecule-padding-x)"
        >
          <Button variant="secondary" onClick={flow.handleClose}>
            {t("common:areYouSureModal.cancelText")}
          </Button>
          {!flow.hasConfirmLaunched && (
            <Button
              variant="primary"
              disabled={!flow.canConfirm || flow.isConfirmRequestPending}
              loading={flow.isConfirmRequestPending}
              onClick={flow.handleConfirm}
            >
              {flow.response !== null && flow.canConfirm
                ? t(
                    "dataTransfer:import.ecoModulation.summaryModal.confirmButton.label",
                    { count: flow.response.summary.validRows }
                  )
                : t(
                    "dataTransfer:import.ecoModulation.summaryModal.continueButton"
                  )}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  )
}
